import { STATUS } from 'framework-asset-preview-lib/internal/shared/constants/assetPreview';
import { OBJECT_TYPE } from 'framework-asset-preview-lib/internal/shared/constants/objectTypes';
import { getAdCampaignStatus } from 'framework-asset-preview-lib/internal/objectTypes/0-31/utils';
import { getContentStatus } from 'framework-asset-preview-lib/internal/objectTypes/0-10/utils';
export function hasProperty(crmObject, propertyName) {
  return Boolean(crmObject.properties && propertyName in crmObject.properties);
}

/**
 * Returns the value of @propertyName in @crmObject.
 *
 * If @propertyName is not present:
 *   - returns undefined when @defaultValue is not provided.
 *   - returns @defaultValue otherwise.
 * @param crmObject The CRM object
 * @param propertyName The property name
 * @param defaultValue Optional default value if @propertyName is not found in @crmObject
 */
export function getPropertyValue(crmObject, propertyName, defaultValue) {
  if (hasProperty(crmObject, propertyName)) {
    return crmObject.properties[propertyName].value;
  }
  return defaultValue;
}
export function getCrmObjectFromAssociatedObjects(crmObject, associationName) {
  const associatedObjects = getAllCrmObjectFromAssociatedObjects(crmObject, associationName);
  return associatedObjects === null || associatedObjects === void 0 ? void 0 : associatedObjects[0];
}
export function getAllCrmObjectFromAssociatedObjects(crmObject, associationName) {
  if (!crmObject.associatedObjects || !crmObject.associatedObjects[associationName] || crmObject.associatedObjects[associationName].length === 0) {
    return undefined;
  }
  return crmObject.associatedObjects[associationName];
}
export function getStatusPropertyName(crmObject) {
  switch (crmObject.objectTypeId) {
    case OBJECT_TYPE.AD_CAMPAIGN:
    case OBJECT_TYPE.AD_GROUP:
    case OBJECT_TYPE.SOCIAL_BROADCAST:
      return 'hs_status';
    case OBJECT_TYPE.FORM:
      return 'hs_status';
    case OBJECT_TYPE.MARKETING_EMAIL:
      return 'hs_publish_status';
    case OBJECT_TYPE.BLOG_POST:
    case OBJECT_TYPE.CONTENT:
    case OBJECT_TYPE.LANDING_PAGE:
    case OBJECT_TYPE.SITE_PAGE:
      return 'state';
    case OBJECT_TYPE.TASK:
      return 'hs_task_status';
    case OBJECT_TYPE.PUBLISHING_TASK:
      return 'hs_publishing_task_state';
    case OBJECT_TYPE.WEB_INTERACTIVE:
      return 'hs_publish_state';
    case OBJECT_TYPE.WORKFLOW:
    case OBJECT_TYPE.AUTOMATION_JOURNEY:
      return 'hs_enabled';
    case OBJECT_TYPE.MARKETING_EVENT:
      return 'hs_event_status';
    case OBJECT_TYPE.MARKETING_SMS:
      return 'hs_state';
    case OBJECT_TYPE.CAMPAIGN:
      return 'hs_campaign_status';
    case OBJECT_TYPE.PLAYBOOK:
      return 'hs_publication_stage';
    case OBJECT_TYPE.EMAIL:
      return 'hs_email_status';
    case OBJECT_TYPE.CALL:
      return 'hs_call_status';
    case OBJECT_TYPE.MEETING_EVENT:
      return 'hs_meeting_outcome';
    case OBJECT_TYPE.KNOWLEDGE_ARTICLE:
      return 'hs_resolved_state';
    case OBJECT_TYPE.WHATSAPP:
      return 'hs_display_status';
    default:
      return null;
  }
}
export function getStatusPropertyValue(crmObject, nonCrmObject) {
  var _getPropertyValue;
  const statusPropertyName = getStatusPropertyName(crmObject);
  if (!statusPropertyName) {
    return null;
  }
  switch (crmObject.objectTypeId) {
    default:
      return getPropertyValue(crmObject, statusPropertyName);
    case OBJECT_TYPE.AD_CAMPAIGN:
    case OBJECT_TYPE.AD_GROUP:
      {
        const hsStatus = getPropertyValue(crmObject, 'hs_status');
        const startDate = getPropertyValue(crmObject, 'hs_start_date');
        const endDate = getPropertyValue(crmObject, 'hs_end_date');
        const adCampaignStatus = getAdCampaignStatus(`${hsStatus}`, parseInt(`${startDate}`, 10), endDate ? parseInt(`${endDate}`, 10) : undefined);
        return adCampaignStatus;
      }
    case OBJECT_TYPE.BLOG_POST:
    case OBJECT_TYPE.CONTENT:
    case OBJECT_TYPE.LANDING_PAGE:
    case OBJECT_TYPE.SITE_PAGE:
      return getContentStatus(crmObject, nonCrmObject);
    case OBJECT_TYPE.WORKFLOW:
    case OBJECT_TYPE.AUTOMATION_JOURNEY:
      {
        const hsEnabled = getPropertyValue(crmObject, 'hs_enabled', 'false');
        return hsEnabled === 'true' ? STATUS.TURNED_ON : STATUS.TURNED_OFF;
      }
    case OBJECT_TYPE.CAMPAIGN:
      return (_getPropertyValue = getPropertyValue(crmObject, statusPropertyName)) === null || _getPropertyValue === void 0 ? void 0 : _getPropertyValue.toUpperCase();
  }
}

/**
 * Set property value and return a new CrmObject
 * @param crmObject The CRM Object.
 * @param propertyName Property name to be updated.
 * @param value Property value.
 */
export function setPropertyValue(crmObject, propertyName, value) {
  let result = Object.assign({}, crmObject);

  // Create property if it doesn't exist.
  if (!hasProperty(result, propertyName)) {
    result = Object.assign({}, result, {
      properties: Object.assign({}, result.properties, {
        [propertyName]: {
          value: ''
        }
      })
    });
  }
  result = Object.assign({}, result, {
    properties: Object.assign({}, result.properties, {
      [propertyName]: Object.assign({}, result.properties[propertyName], {
        value
      })
    })
  });
  return result;
}
export function getNameProperty(objectTypeId) {
  switch (objectTypeId) {
    default:
      return 'hs_name';
    case OBJECT_TYPE.SOCIAL_BROADCAST:
      return 'hs_content_body';
    case OBJECT_TYPE.TASK:
      return 'hs_task_subject';
    case OBJECT_TYPE.MARKETING_EVENT:
      return 'hs_event_name';
    case OBJECT_TYPE.CONTACT:
      return 'hs_full_name_or_email';
  }
}
export function getLinkProperty(objectTypeId, primaryDisplayLabelPropertyName) {
  switch (objectTypeId) {
    default:
      return primaryDisplayLabelPropertyName || 'hs_object_id';
    case OBJECT_TYPE.CONTACT:
      return 'hs_full_name_or_email';
  }
}